import React from 'react';
import { X, CheckCircle, Building, FileText } from 'lucide-react';

const ProposalDetailsModal = ({
  selectedProposal,
  getStatusColor,
  getPriorityColor,
  onClose,
  onEdit,
  onDownloadPDF
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">Proposal Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Proposal Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Project Title</p>
              <h3 className="text-xl font-semibold text-gray-900">{selectedProposal.title}</h3>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Property</p>
              <p className="text-gray-800">{selectedProposal.property}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Proposed Amount</p>
              <p className="text-2xl font-bold text-green-600">{selectedProposal.proposedAmount}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Estimated Duration</p>
              <p className="text-gray-800">{selectedProposal.estimatedDuration}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Submission Date</p>
              <p className="text-gray-800">{selectedProposal.submittedDate}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Project Deadline</p>
              <p className="text-gray-800">{selectedProposal.deadline}</p>
            </div>
          </div>

          {/* Status & Priority Badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProposal.status)}`}>
              Status: {selectedProposal.status}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedProposal.priority)}`}>
              Priority: {selectedProposal.priority}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium text-blue-600 bg-blue-100">
              Service: {selectedProposal.serviceType}
            </span>
          </div>

          {/* Project Description */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-2">Project Description</p>
            <p className="text-gray-800 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{selectedProposal.description}</p>
          </div>

          {/* Work Scope */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
              Work Scope
            </h4>
            <ul className="space-y-2">
              {selectedProposal.workScope?.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Materials & Equipment */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <Building className="w-5 h-5 mr-2 text-green-600" />
              Materials & Equipment
            </h4>
            <ul className="space-y-2">
              {selectedProposal.materials?.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Client Contact Information */}
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-purple-600" />
              Client Contact
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p><strong>Name:</strong> {selectedProposal.clientContact?.name}</p>
                <p><strong>Role:</strong> {selectedProposal.clientContact?.role}</p>
              </div>
              <div>
                <p><strong>Phone:</strong> {selectedProposal.clientContact?.phone}</p>
                <p><strong>Email:</strong> {selectedProposal.clientContact?.email}</p>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          {selectedProposal.notes && (
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Additional Notes</p>
              <p className="text-gray-800 bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">{selectedProposal.notes}</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-between p-6 border-t border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(selectedProposal)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit Proposal
            </button>
            <button
              onClick={onDownloadPDF}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Download PDF
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProposalDetailsModal;
